import { Injectable, computed, signal } from '@angular/core';

export type NotificationType = 'match' | 'entretien' | 'cv' | 'offre' | 'system';
export type NotificationFilter = 'all' | 'unread';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  date: string;
  read: boolean;
  link?: string;
}

const minsAgo = (n: number): string =>
  new Date(Date.now() - n * 60_000).toISOString();

const SEED: AppNotification[] = [
  {
    id: 'n1',
    type: 'match',
    title: 'Nouveau match suggéré',
    message: 'Léa Fontaine ↔ Lead Designer Produit (TechVision SAS) — score 88%.',
    date: minsAgo(12),
    read: false,
    link: '/matching',
  },
  {
    id: 'n2',
    type: 'entretien',
    title: 'Entretien confirmé',
    message: 'Sophie Marchand — Lead Développeur React, mardi 16 juin à 10h00.',
    date: minsAgo(95),
    read: false,
    link: '/entretiens',
  },
  {
    id: 'n3',
    type: 'cv',
    title: 'Correction de CV demandée',
    message: 'Une correction a été demandée sur le CV de Novia Recrutement.',
    date: minsAgo(240),
    read: false,
    link: '/cvtheque',
  },
  {
    id: 'n4',
    type: 'offre',
    title: 'Offre urgente publiée',
    message: 'Responsable Commercial Régional (Innova Commerce) marquée urgente.',
    date: minsAgo(560),
    read: true,
    link: '/offres',
  },
  {
    id: 'n5',
    type: 'system',
    title: 'Bienvenue sur iMatch',
    message: 'Votre espace de placement est prêt. Bonne journée !',
    date: minsAgo(1440),
    read: true,
  },
];

@Injectable({ providedIn: 'root' })
export class NotificationsStore {
  private _notifications = signal<AppNotification[]>(SEED);
  private _filter = signal<NotificationFilter>('all');

  readonly notifications = this._notifications.asReadonly();
  readonly filter = this._filter.asReadonly();

  readonly unreadCount = computed(
    () => this._notifications().filter((n) => !n.read).length,
  );

  readonly filtered = computed(() =>
    this._filter() === 'unread'
      ? this._notifications().filter((n) => !n.read)
      : this._notifications(),
  );

  readonly recent = computed(() => this._notifications().slice(0, 5));

  setFilter(filter: NotificationFilter): void {
    this._filter.set(filter);
  }

  markRead(id: string): void {
    this._notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }

  markAllRead(): void {
    this._notifications.update((list) =>
      list.map((n) => ({ ...n, read: true })),
    );
  }

  remove(id: string): void {
    this._notifications.update((list) => list.filter((n) => n.id !== id));
  }
}
